<template>
  <modal
    v-if="open"
    :title="$t('Inventory.add_car_title')"
    closable
    @close-request="requestClose"
  >
    <form class="inventory-form" @submit.prevent="submit">
      <div class="inventory-form__intro">
        <p>{{ $t('Inventory.add_car_intro') }}</p>
      </div>

      <section class="inventory-form__section">
        <div class="inventory-form__section-title">
          <span>1</span>
          <div>
            <h4>{{ $t('Inventory.billing_account') }}</h4>
            <p>{{ $t('Inventory.billing_account_help') }}</p>
          </div>
        </div>
        <label class="form-group">
          <span>{{ $t('Inventory.billing_name') }} *</span>
          <select v-model="payerUID" class="form-control" required @change="onAccountChange">
            <option value="">{{ $t('Inventory.choose_billing_account') }}</option>
            <option v-for="account in accounts" :key="accountId(account)" :value="accountId(account)">
              {{ account.name || account.value || account.payerName }}
            </option>
          </select>
        </label>
        <div v-if="loadingDealer" class="inventory-inline-state">
          <i class="fa fa-spinner fa-spin" aria-hidden="true" />
          {{ $t('Inventory.loading_account') }}
        </div>
        <div v-else-if="selectedDealer" class="inventory-account-preview">
          <i class="fa fa-map-marker" aria-hidden="true" />
          <div>
            <strong>{{ selectedDealer.name }}</strong>
            <span>{{ dealerAddress }}</span>
          </div>
        </div>
      </section>

      <section class="inventory-form__section">
        <div class="inventory-form__section-title">
          <span>2</span>
          <div>
            <h4>{{ $t('Inventory.order_details') }}</h4>
            <p>{{ $t('Inventory.order_details_help') }}</p>
          </div>
        </div>
        <div class="inventory-form__grid inventory-form__grid--three">
          <label class="form-group">
            <span>{{ $t('Inventory.source') }} *</span>
            <input v-model.trim="orderDetails.source" class="form-control" type="text" required>
          </label>
          <label class="form-group">
            <span>{{ $t('Inventory.reference') }}</span>
            <input v-model.trim="orderDetails.numRef" class="form-control" type="text">
          </label>
          <label class="form-group">
            <span>{{ $t('Inventory.date_entered') }} *</span>
            <input v-model="entryDateInput" class="form-control" type="datetime-local" required>
          </label>
        </div>
        <label class="form-group">
          <span>{{ $t('Inventory.comments') }}</span>
          <textarea v-model.trim="orderDetails.comment" class="form-control" rows="2" />
        </label>
        <div class="inventory-form__grid">
          <label class="form-group">
            <span>{{ $t('Inventory.dropoff_person') }} *</span>
            <input v-model.trim="dropOffPerson" class="form-control" type="text" required>
          </label>
          <label class="form-group">
            <span>{{ $t('Inventory.dropoff_organization') }} *</span>
            <input
              v-model.trim="dropOffOrganization"
              class="form-control"
              type="text"
              required
              @input="dropOffOrganizationTouched = true"
            >
          </label>
        </div>
      </section>

      <section class="inventory-form__section">
        <div class="inventory-form__section-title">
          <span>3</span>
          <div>
            <h4>{{ $t('Inventory.dispatch') }}</h4>
            <p>{{ $t('Inventory.dispatch_help') }}</p>
          </div>
        </div>
        <label class="form-group">
          <span>{{ $t('Inventory.dispatch_driver') }} *</span>
          <select v-model="driverUID" class="form-control" required @change="driverSelectionTouched = true">
            <option value="">{{ $t('Inventory.choose_driver') }}</option>
            <option v-for="driver in drivers" :key="driverId(driver)" :value="driverId(driver)">
              {{ driverName(driver) }}
            </option>
          </select>
        </label>
      </section>

      <section class="inventory-form__section">
        <div class="inventory-form__section-title">
          <span>4</span>
          <div>
            <h4>{{ $t('Inventory.vehicles') }}</h4>
            <p>{{ $t('Inventory.vehicles_help') }}</p>
          </div>
        </div>

        <div class="inventory-vehicle-editor">
          <div class="inventory-form__grid inventory-form__grid--vin">
            <label class="form-group">
              <span>{{ $t('Inventory.vin') }} *</span>
              <input
                :value="vehicleDraft.vin"
                class="form-control inventory-vin-input"
                type="text"
                maxlength="30"
                @input="setVin($event.target.value)"
              >
            </label>
            <button
              type="button"
              class="btn btn-light inventory-decode-button"
              :disabled="decodingVin || vehicleDraft.vin.length < 8"
              @click="decodeVin"
            >
              <i :class="['fa', decodingVin ? 'fa-spinner fa-spin' : 'fa-search']" aria-hidden="true" />
              {{ decodingVin ? $t('Inventory.decoding_vin') : $t('Inventory.decode_vin') }}
            </button>
          </div>

          <div v-if="vinFeedback" class="inventory-form__message inventory-form__message--warning">
            {{ vinFeedback }}
          </div>

          <div class="inventory-form__grid inventory-form__grid--four">
            <label class="form-group">
              <span>{{ $t('Inventory.year') }} *</span>
              <input v-model.trim="vehicleDraft.year" class="form-control" type="text" maxlength="4">
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.make') }} *</span>
              <input v-model.trim="vehicleDraft.make" class="form-control" type="text">
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.model') }} *</span>
              <input v-model.trim="vehicleDraft.model" class="form-control" type="text">
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.trim') }}</span>
              <select v-if="trimOptions.length" v-model="vehicleDraft.trim" class="form-control">
                <option value="">{{ $t('Inventory.choose_trim') }}</option>
                <option v-for="trim in trimOptions" :key="trim" :value="trim">{{ trim }}</option>
              </select>
              <input v-else v-model.trim="vehicleDraft.trim" class="form-control" type="text">
            </label>
          </div>

          <div class="inventory-form__grid inventory-form__grid--four">
            <label class="form-group">
              <span>{{ $t('Inventory.lot') }} *</span>
              <select v-model="vehicleDraft.inventoryLot" class="form-control" required>
                <option value="">{{ $t('Inventory.choose_lot') }}</option>
                <option v-for="lot in inventoryLots" :key="lot" :value="lot">{{ lot }}</option>
              </select>
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.size') }}</span>
              <input v-model.number="vehicleDraft.size" class="form-control" type="number" min="0" step="1">
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.volumetric_weight') }}</span>
              <input v-model.number="vehicleDraft.weight" class="form-control" type="number" min="0" step="0.01">
            </label>
            <label class="form-group">
              <span>{{ $t('Inventory.real_weight') }}</span>
              <input v-model.number="vehicleDraft.realWeight" class="form-control" type="number" min="0" step="0.01">
            </label>
          </div>

          <div class="inventory-vehicle-term">
            <span>{{ $t('Inventory.storage_term') }}</span>
            <strong>{{ $t('Inventory.long_term') }}</strong>
          </div>

          <div class="inventory-vehicle-date">
            <label>
              <input v-model="vehicleDraft.useDateOverride" type="checkbox">
              {{ $t('Inventory.custom_entry_date') }}
            </label>
            <input
              v-if="vehicleDraft.useDateOverride"
              v-model="vehicleDraft.dateInput"
              class="form-control"
              type="datetime-local"
            >
            <small v-else>{{ $t('Inventory.uses_default_entry_date') }}</small>
          </div>

          <div class="inventory-vehicle-editor__actions">
            <button type="button" class="btn btn-light" @click="resetVehicleDraft">
              {{ $t('Inventory.clear') }}
            </button>
            <button type="button" class="btn btn-kne-orange" :disabled="!vehicleDraftValid" @click="saveVehicle">
              <i class="fa fa-plus-circle mr-1" aria-hidden="true" />
              {{ editingVehicleIndex === null ? $t('Inventory.add_vehicle') : $t('Inventory.save_vehicle') }}
            </button>
          </div>
        </div>

        <div v-if="vehicles.length" class="inventory-vehicle-list">
          <div v-for="(vehicle, index) in vehicles" :key="`${vehicle.vin}-${index}`" class="inventory-vehicle-row">
            <div class="inventory-vehicle-row__number">{{ index + 1 }}</div>
            <div class="inventory-vehicle-row__body">
              <strong>{{ [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(' ') }}</strong>
              <span>{{ vehicle.vin }}</span>
              <small>
                {{ $t('Inventory.lot') }}: {{ vehicle.inventoryLot }}
              </small>
              <small>
                {{ $t('Inventory.storage_term') }}: {{ $t('Inventory.long_term') }}
              </small>
              <small v-if="vehicle.dateEntered">
                {{ $t('Inventory.date_entered') }}: {{ formatDate(vehicle.dateEntered) }}
              </small>
            </div>
            <button type="button" class="btn btn-sm btn-light" :title="$t('Inventory.edit')" @click="editVehicle(index)">
              <i class="fa fa-pencil" aria-hidden="true" />
            </button>
            <button type="button" class="btn btn-sm btn-light text-danger" :title="$t('Inventory.remove')" @click="removeVehicle(index)">
              <i class="fa fa-trash" aria-hidden="true" />
            </button>
          </div>
        </div>
        <div v-else class="inventory-empty-list">
          <i class="fa fa-car" aria-hidden="true" />
          {{ $t('Inventory.no_vehicles_added') }}
        </div>
      </section>

      <div v-if="feedback" class="inventory-form__message" :class="feedbackClass" role="alert">
        {{ feedback }}
      </div>

      <div v-if="createdOrderIds.length" class="inventory-created-summary">
        <strong>{{ $t('Inventory.created_orders') }}</strong>
        <span v-for="orderId in createdOrderIds" :key="orderId">{{ orderId }}</span>
      </div>

      <div v-if="failedDispatchResults.length" class="inventory-retry-row">
        <span>
          {{ $t('Inventory.dispatch_failed_count') }}: {{ failedDispatchResults.length }}
          ({{ failedDispatchResults.map(result => result.orderId).join(', ') }}).
          {{ $t('Inventory.retry_before_closing') }}
        </span>
        <button type="button" class="btn btn-light" :disabled="submitting" @click="retryFailedDispatches">
          <i class="fa fa-refresh mr-1" aria-hidden="true" />
          {{ $t('Inventory.retry_dispatch') }}
        </button>
      </div>

      <footer class="inventory-form__footer">
        <button type="button" class="btn btn-light" :disabled="submitting || failedDispatchResults.length > 0" @click="requestClose">
          {{ $t('Inventory.cancel') }}
        </button>
        <button type="submit" class="btn btn-kne-orange" :disabled="!formValid || submitting || creationCommitted">
          <i :class="['fa', submitting ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
          {{ submitting ? $t('Inventory.creating_cars') : $t('Inventory.create_and_dispatch') }}
        </button>
      </footer>
    </form>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import { db } from '../../../../firebase'
import { buildManualInventoryOrder } from '../../../../inventory/inventoryOrderFactory'
import {
  INVENTORY_TERM,
  isDefaultInventoryDriverName
} from '../../../../inventory/inventorySelectors'
import { createInventoryBatch, retryInventoryDispatch } from '../../../../inventory/inventoryService'
import { formatTorontoDateTime, fromTorontoDateTimeInput, toTorontoDateTimeInput } from './inventoryDateTime'

const emptyVehicle = dateInput => ({
  vin: '',
  year: '',
  make: '',
  model: '',
  trim: '',
  inventoryLot: '',
  inventoryTerm: INVENTORY_TERM.LONG_TERM,
  size: 1,
  weight: 0,
  realWeight: 0,
  useDateOverride: false,
  dateInput: dateInput || toTorontoDateTimeInput()
})

export default {
  name: 'AddInventoryCarsModal',

  components: {
    Modal
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    accounts: {
      type: Array,
      default: () => []
    },
    drivers: {
      type: Array,
      default: () => []
    },
    existingOrders: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      payerUID: '',
      selectedDealer: null,
      loadingDealer: false,
      orderDetails: {
        source: 'Inventory',
        numRef: '',
        comment: ''
      },
      entryDateInput: toTorontoDateTimeInput(),
      dropOffPerson: '',
      dropOffOrganization: '',
      dropOffOrganizationTouched: false,
      driverUID: '',
      driverSelectionTouched: false,
      vehicleDraft: emptyVehicle(),
      trimOptions: [],
      vehicles: [],
      editingVehicleIndex: null,
      decodingVin: false,
      vinFeedback: '',
      feedback: '',
      feedbackType: 'error',
      submitting: false,
      creationCommitted: false,
      createdOrderIds: [],
      dispatchResults: []
    }
  },

  computed: {
    inventoryLots () {
      return this.$store.getters.inventoryLotNames
    },
    companyUID () {
      return this.$store.state.authorization && this.$store.state.authorization.company
    },
    operator () {
      const user = this.$store.state.user || {}
      const userInfo = this.$store.state.userInfo || {}
      return {
        uid: user.uid || '',
        name: userInfo.name || user.displayName || user.email || ''
      }
    },
    selectedDriver () {
      const driver = this.drivers.find(driver => this.driverId(driver) === this.driverUID)
      return driver
        ? { uid: this.driverId(driver), name: this.driverName(driver) }
        : null
    },
    dealerAddress () {
      if (!this.selectedDealer) return ''
      return [
        this.selectedDealer.address,
        this.selectedDealer.city,
        this.selectedDealer.province,
        this.selectedDealer.postcode
      ].filter(Boolean).join(', ')
    },
    vehicleDraftValid () {
      return Boolean(
        this.vehicleDraft.vin.trim() &&
        this.vehicleDraft.year &&
        this.vehicleDraft.make.trim() &&
        this.vehicleDraft.model.trim() &&
        this.inventoryLots.indexOf(this.vehicleDraft.inventoryLot) !== -1 &&
        this.vehicleDraft.inventoryTerm === INVENTORY_TERM.LONG_TERM &&
        (!this.vehicleDraft.useDateOverride || !isNaN(fromTorontoDateTimeInput(this.vehicleDraft.dateInput)))
      )
    },
    formValid () {
      return Boolean(
        this.companyUID &&
        this.payerUID &&
        this.selectedDealer &&
        this.orderDetails.source &&
        !isNaN(fromTorontoDateTimeInput(this.entryDateInput)) &&
        this.dropOffPerson &&
        this.dropOffOrganization &&
        this.selectedDriver &&
        this.vehicles.length &&
        this.vehicles.every(vehicle => {
          return this.inventoryLots.indexOf(vehicle.inventoryLot) !== -1 &&
            vehicle.inventoryTerm === INVENTORY_TERM.LONG_TERM
        })
      )
    },
    feedbackClass () {
      return this.feedbackType === 'success'
        ? 'inventory-form__message--success'
        : 'inventory-form__message--error'
    },
    failedDispatchResults () {
      return this.dispatchResults.filter(result => !result.queued)
    }
  },

  watch: {
    open (value) {
      if (value) {
        this.reset()
        this.$store.dispatch('loadInventoryLots')
      }
    },
    drivers () {
      if (this.open && !this.driverSelectionTouched && !this.driverUID) {
        this.preselectInventoryDriver()
      }
    }
  },

  methods: {
    accountId (account) {
      return String(account['.key'] || account.key || account.uid || '')
    },
    driverId (driver) {
      return String(driver.uid || driver.key || driver['.key'] || '')
    },
    driverName (driver) {
      return driver.name || driver.value || driver['.value'] || ''
    },
    normalizeVin (vin) {
      return String(vin || '').replace(/\s/g, '').toUpperCase()
    },
    setVin (vin) {
      this.vehicleDraft.vin = this.normalizeVin(vin)
      this.vinFeedback = ''
    },
    preselectInventoryDriver () {
      if (this.driverSelectionTouched || this.driverUID) return
      const driver = this.drivers.find(driver => isDefaultInventoryDriverName(this.driverName(driver)))
      if (driver) this.driverUID = this.driverId(driver)
    },
    async onAccountChange () {
      const account = this.accounts.find(account => this.accountId(account) === this.payerUID)
      this.selectedDealer = null
      if (!account) return

      this.loadingDealer = true
      try {
        const snapshot = await db.ref(`users/dealers/${this.payerUID}`).once('value')
        this.selectedDealer = {
          ...account,
          ...(snapshot.val() || {}),
          name: (snapshot.val() && snapshot.val().name) || account.name || account.value || ''
        }
        if (!this.dropOffOrganizationTouched || !this.dropOffOrganization) {
          this.dropOffOrganization = this.selectedDealer.name || ''
        }
      } catch (error) {
        this.feedback = this.$t('Inventory.account_load_error')
        this.feedbackType = 'error'
      } finally {
        this.loadingDealer = false
      }
    },
    async decodeVin () {
      this.decodingVin = true
      this.vinFeedback = ''
      try {
        const response = await window.axios.get(`${process.env.API_ENDPOINT}/decode/decodevin/${this.vehicleDraft.vin}`)
        const decoded = response.data || {}
        if (decoded.error) throw new Error(decoded.error)
        this.vehicleDraft.make = decoded.make || this.vehicleDraft.make
        this.vehicleDraft.model = decoded.model || this.vehicleDraft.model
        this.vehicleDraft.year = decoded.year ? String(decoded.year) : this.vehicleDraft.year
        this.trimOptions = (decoded.trims || [])
          .map(item => String(item.trimName || item.name || item).replace(/[.$#[\]/]/g, ''))
          .filter(Boolean)
        if (this.trimOptions.length) this.vehicleDraft.trim = this.trimOptions[0]
      } catch (error) {
        this.vinFeedback = error.message || this.$t('Inventory.vin_decode_error')
      } finally {
        this.decodingVin = false
      }
    },
    isDuplicateVin (vin, ignoredIndex = null) {
      const normalized = this.normalizeVin(vin)
      const duplicateInDraft = this.vehicles.some((vehicle, index) => {
        return index !== ignoredIndex && this.normalizeVin(vehicle.vin) === normalized
      })
      const duplicateInInventory = this.existingOrders.some(order => {
        return !order.inventoryBilledAt && this.normalizeVin(order.vin) === normalized
      })
      return duplicateInDraft || duplicateInInventory
    },
    saveVehicle () {
      if (!this.vehicleDraftValid) return
      if (this.isDuplicateVin(this.vehicleDraft.vin, this.editingVehicleIndex)) {
        this.vinFeedback = this.$t('Inventory.active_vin_duplicate')
        return
      }

      const vehicle = {
        ...this.vehicleDraft,
        vin: this.normalizeVin(this.vehicleDraft.vin),
        size: Number(this.vehicleDraft.size) || 0,
        weight: Number(this.vehicleDraft.weight) || 0,
        realWeight: Number(this.vehicleDraft.realWeight) || 0,
        dateEntered: this.vehicleDraft.useDateOverride
          ? fromTorontoDateTimeInput(this.vehicleDraft.dateInput)
          : null
      }
      if (this.editingVehicleIndex === null) this.vehicles.push(vehicle)
      else this.$set(this.vehicles, this.editingVehicleIndex, vehicle)
      this.resetVehicleDraft()
    },
    editVehicle (index) {
      this.vehicleDraft = {...this.vehicles[index]}
      this.trimOptions = []
      this.editingVehicleIndex = index
      this.vinFeedback = ''
    },
    removeVehicle (index) {
      this.vehicles.splice(index, 1)
      if (this.editingVehicleIndex === index) this.resetVehicleDraft()
    },
    resetVehicleDraft () {
      this.vehicleDraft = emptyVehicle(this.entryDateInput)
      this.trimOptions = []
      this.editingVehicleIndex = null
      this.vinFeedback = ''
    },
    async submit () {
      if (!this.formValid || this.submitting || this.creationCommitted) return
      this.submitting = true
      this.feedback = ''
      this.createdOrderIds = []

      try {
        const dateEntered = fromTorontoDateTimeInput(this.entryDateInput)
        const orders = this.vehicles.map(vehicle => buildManualInventoryOrder({
          dealer: this.selectedDealer,
          dealerId: this.payerUID,
          vehicle,
          inventoryLot: vehicle.inventoryLot,
          inventoryTerm: INVENTORY_TERM.LONG_TERM,
          orderDetails: {...this.orderDetails},
          dateEntered: vehicle.dateEntered || dateEntered,
          dropOffPerson: this.dropOffPerson,
          dropOffOrganization: this.dropOffOrganization,
          operator: this.operator,
          zones: this.$store.state.zones,
          transporter: this.$store.state.userInfo || {}
        }))
        const result = await createInventoryBatch({
          companyUID: this.companyUID,
          orders,
          driver: this.selectedDriver,
          operator: this.operator
        })

        this.creationCommitted = true
        this.createdOrderIds = result.createdOrderIds || []
        this.dispatchResults = result.dispatchResults || []
        if (result.allDispatchesQueued !== false) {
          this.feedback = this.$t('Inventory.create_success')
          this.feedbackType = 'success'
          this.$emit('completed', result)
          this.$emit('close')
        } else {
          this.feedback = this.$t('Inventory.created_dispatch_partial_error')
          this.feedbackType = 'error'
          this.$emit('partial', result)
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.create_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    async retryFailedDispatches () {
      if (!this.failedDispatchResults.length || !this.selectedDriver) return
      this.submitting = true
      try {
        const retried = await Promise.all(this.failedDispatchResults.map((result, index) => {
          return retryInventoryDispatch({
            companyUID: this.companyUID,
            orderId: result.orderId,
            driver: this.selectedDriver,
            deliveryOrder: (result.payload && result.payload.deliveryOrder) || index + 1
          })
        }))
        const retriedById = retried.reduce((results, item) => {
          results[item.orderId] = item
          return results
        }, {})
        this.dispatchResults = this.dispatchResults.map(result => retriedById[result.orderId] || result)
        if (!this.failedDispatchResults.length) {
          this.feedback = this.$t('Inventory.dispatch_retry_success')
          this.feedbackType = 'success'
          this.$emit('completed', {
            createdOrderIds: this.createdOrderIds,
            dispatchResults: this.dispatchResults,
            allDispatchesQueued: true
          })
          this.$emit('close')
        } else {
          this.feedback = this.$t('Inventory.dispatch_retry_error')
          this.feedbackType = 'error'
        }
      } catch (error) {
        this.feedback = error.message || this.$t('Inventory.dispatch_retry_error')
        this.feedbackType = 'error'
      } finally {
        this.submitting = false
      }
    },
    requestClose () {
      if (!this.submitting && !this.failedDispatchResults.length) this.$emit('close')
    },
    formatDate (timestamp) {
      return formatTorontoDateTime(timestamp)
    },
    reset () {
      this.payerUID = ''
      this.selectedDealer = null
      this.loadingDealer = false
      this.orderDetails = {source: 'Inventory', numRef: '', comment: ''}
      this.entryDateInput = toTorontoDateTimeInput()
      this.dropOffPerson = ''
      this.dropOffOrganization = ''
      this.dropOffOrganizationTouched = false
      this.driverUID = ''
      this.driverSelectionTouched = false
      this.vehicles = []
      this.resetVehicleDraft()
      this.feedback = ''
      this.feedbackType = 'error'
      this.submitting = false
      this.creationCommitted = false
      this.createdOrderIds = []
      this.dispatchResults = []
      this.preselectInventoryDriver()
    }
  }
}
</script>

<style scoped>
.inventory-form {
  width: min(1040px, 80vw);
  max-width: 100%;
  padding: 0 22px 22px;
  color: #344150;
}

.inventory-form__intro {
  padding: 16px 20px;
  margin: 0 -22px 20px;
  color: #536273;
  background: #f3f6f8;
}

.inventory-form__intro p,
.inventory-form__section-title p {
  margin: 0;
}

.inventory-form__section {
  padding: 20px;
  margin-bottom: 18px;
  border: 1px solid #e3e8ed;
  border-radius: 13px;
  background: #fff;
}

.inventory-form__section-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}

.inventory-form__section-title > span {
  display: flex;
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  background: #e68a28;
  font-weight: 800;
}

.inventory-form__section-title h4 {
  margin: 0 0 2px;
  color: #273543;
  font-size: 1rem;
}

.inventory-form__section-title p {
  color: #7b8794;
  font-size: .82rem;
}

.inventory-form label > span {
  display: block;
  margin-bottom: 6px;
  color: #536170;
  font-size: .82rem;
  font-weight: 700;
}

.inventory-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.inventory-form__grid--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.inventory-form__grid--four {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.inventory-form__grid--vin {
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
}

.inventory-inline-state,
.inventory-account-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 9px;
  background: #f5f7f9;
}

.inventory-account-preview i {
  color: #dd8121;
  font-size: 1.2rem;
}

.inventory-account-preview div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.inventory-account-preview span {
  color: #75818d;
  font-size: .82rem;
}

.inventory-vehicle-editor {
  padding: 16px;
  border-radius: 10px;
  background: #f6f8fa;
}

.inventory-vin-input {
  font-family: monospace;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.inventory-decode-button {
  min-height: 38px;
  margin-bottom: 16px;
  white-space: nowrap;
}

.inventory-vehicle-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
}

.inventory-vehicle-date {
  display: flex;
  min-height: 42px;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 14px;
  border: 1px solid #e1e6ea;
  border-radius: 8px;
  background: #fff;
}

.inventory-vehicle-term {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  margin-bottom: 10px;
  border: 1px solid #f1d1aa;
  border-radius: 8px;
  color: #6e4b24;
  background: #fff8ef;
}

.inventory-vehicle-term span {
  font-size: .82rem;
  font-weight: 700;
}

.inventory-vehicle-term strong {
  padding: 3px 9px;
  border-radius: 999px;
  color: #8a5419;
  background: #ffe7c8;
  font-size: .76rem;
}

.inventory-vehicle-date label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  white-space: nowrap;
}

.inventory-vehicle-date .form-control {
  max-width: 240px;
}

.inventory-vehicle-date small {
  color: #84909b;
}

.inventory-vehicle-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}

.inventory-vehicle-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: 1px solid #e4e8ed;
  border-radius: 9px;
}

.inventory-vehicle-row__number {
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #8a5419;
  background: #fff0dd;
  font-size: .78rem;
  font-weight: 800;
}

.inventory-vehicle-row__body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.inventory-vehicle-row__body span {
  overflow-wrap: anywhere;
  color: #718091;
  font-family: monospace;
  font-size: .8rem;
}

.inventory-vehicle-row__body small {
  color: #8a6a42;
  font-size: .72rem;
}

.inventory-empty-list {
  padding: 18px;
  margin-top: 14px;
  border: 1px dashed #ccd3da;
  border-radius: 9px;
  color: #7c8793;
  text-align: center;
}

.inventory-form__message {
  padding: 12px 14px;
  margin: 12px 0;
  border-radius: 8px;
}

.inventory-form__message--error {
  color: #8d2f32;
  background: #fde7e7;
}

.inventory-form__message--warning {
  color: #79530d;
  background: #fff0ca;
}

.inventory-form__message--success {
  color: #17613a;
  background: #e2f6e9;
}

.inventory-created-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 12px 0;
}

.inventory-created-summary span {
  padding: 4px 9px;
  border-radius: 999px;
  background: #edf0f3;
  font-size: .78rem;
  font-weight: 700;
}

.inventory-retry-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  margin: 12px 0;
  border: 1px solid #f0c1c1;
  border-radius: 8px;
  color: #812e32;
  background: #fff8f8;
}

.inventory-form__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  bottom: -22px;
  z-index: 2;
  padding: 16px 0 0;
  background: #fff;
}

@media (max-width: 900px) {
  .inventory-form {
    width: 78vw;
  }

  .inventory-form__grid--three,
  .inventory-form__grid--four {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .inventory-form {
    width: 100%;
    padding-right: 14px;
    padding-left: 14px;
  }

  .inventory-form__intro {
    margin-right: -14px;
    margin-left: -14px;
  }

  .inventory-form__section {
    padding: 14px;
  }

  .inventory-form__grid,
  .inventory-form__grid--three,
  .inventory-form__grid--four,
  .inventory-form__grid--vin {
    grid-template-columns: 1fr;
  }

  .inventory-decode-button {
    margin-top: -8px;
  }

  .inventory-form__footer,
  .inventory-vehicle-editor__actions {
    flex-direction: column-reverse;
  }

  .inventory-vehicle-date {
    align-items: stretch;
    flex-direction: column;
  }

  .inventory-vehicle-date .form-control {
    max-width: none;
  }

  .inventory-form__footer .btn,
  .inventory-vehicle-editor__actions .btn {
    width: 100%;
  }
}
</style>
